import { useState, useEffect, useCallback, useRef } from 'react';

interface UseInactivityOptions {
  timeout?: number;
  events?: string[];
  enabled?: boolean;
}

export const useInactivity = ({
  timeout = 30000,
  events = ['pointerdown', 'keydown', 'touchstart'],
  enabled = true
}: UseInactivityOptions = {}) => {
  const [isInactive, setIsInactive] = useState(false);
  const timeoutId = useRef<NodeJS.Timeout>();
  const eventsRef = useRef(events);

  // Actualizar ref si cambian los eventos (sin causar re-ejecución del efecto)
  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    setIsInactive(prev => {
      if (prev) {
        console.log('🟢 Usuario activo de nuevo');
      }
      return false;
    });

    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    timeoutId.current = setTimeout(() => {
      console.log('🔴 Usuario inactivo');
      setIsInactive(true);
    }, timeout);
    console.log(`⏱️ Timer iniciado - inactividad en ${timeout / 1000} segundos`);
  }, [timeout, enabled]);

  useEffect(() => {
    if (!enabled) {
      setIsInactive(false);
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = undefined;
      }
      return;
    }

    // Si ya estamos inactivos (salvapantallas ON), dejamos de escuchar eventos
    if (isInactive) {
      eventsRef.current.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = undefined;
      }
      return;
    }

    // Usuario activo: escuchamos eventos y arrancamos el timer
    eventsRef.current.forEach(event => {
      window.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      eventsRef.current.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = undefined;
      }
    };
  }, [resetTimer, isInactive, enabled]);

  return { isInactive, resetInactivity: resetTimer };
};
